import { useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { anyPass, isEmpty, isNil, equals } from 'ramda'
import { FormikContextType, useFormikContext } from 'formik'
import {
  Stack,
  Text,
  Box,
  Skeleton,
  Columns,
  Column,
  TextLink,
  Checkbox,
  Alert
} from '@machobear/design-system'
import { getFormattedStates } from '@/utils/address'
import useBasic from '@/hooks/use-basic'
import { FieldInput, Dropdown } from '@/components/ds-form'

import BuildingSearchField from './building-search-field'
import ListingMap from './listing-map'
import { CreateFormValues } from '../../types'
import { FormBox } from '../../components'

const DEFAULT_COORDS = {
  lat: 51.903567,
  lng: -97.336333
}
const DEFAULT_ZOOM = 3
const BUILDING_ZOOM = 12

interface Props {
  isLoading: boolean
  isApartment: boolean
}

const isZeroOrEmpty = anyPass<any>([isEmpty, isNil, equals(0), equals('0')])

const LocationSection = ({ isLoading, isApartment }: Props) => {
  const { t } = useTranslation()
  const { values, setValues, setFieldValue }: FormikContextType<CreateFormValues> =
    useFormikContext<CreateFormValues>()
  const { countries }: any = useBasic(['countries'])
  const formattedStates = getFormattedStates(countries, values.country || 'ca')
  const isUnitedStatesSelected = values.country === 'us'
  const isMultiUnit = values.groupListing === '1'
  const isHouse = values.type === 'house'
  const hasBuildingLocation =
    !isZeroOrEmpty(values?.building?.lng) && !isZeroOrEmpty(values?.building?.lat)

  const [enterAddressManually, setEnterAddressManually] = useState(false)

  const zoom = hasBuildingLocation ? BUILDING_ZOOM : DEFAULT_ZOOM
  const markerCoords = hasBuildingLocation
    ? { lat: values.building.lat, lng: values.building.lng }
    : DEFAULT_COORDS

  return (
    <FormBox label={t('Location', 'Location')}>
      {isLoading ? (
        <Skeleton.Shape height="58px" />
      ) : (
        <Stack space="gutter" align="left">
          <BuildingSearchField
            enterAddressManually={enterAddressManually}
            setEnterAddressManually={setEnterAddressManually}
            values={values}
            setValues={setValues}
          />
          {(values.full_address.value || enterAddressManually) && (
            <>
              <Box borderBottom="borderNeutralLight"></Box>
              {enterAddressManually && (
                <Alert tone="caution">
                  <Trans i18nKey="l.listing.create.verification_required.text" t={t}>
                    <Text tone="caution" size="small">
                      To keep our community safe, we manually verify all new buildings which takes
                      1-2 business days. Your listing will be published upon approval.{' '}
                      <TextLink
                        href="https://liv.rent/blog/livrent/how-to-become-verified/#How_to_verify_your_listings"
                        target="_blank"
                        weight="regular"
                      >
                        Learn more
                      </TextLink>
                    </Text>
                  </Trans>
                </Alert>
              )}
              <Columns space="gutter" collapseBelow="tablet">
                <Column>
                  <FieldInput
                    name="address"
                    label={t('l.listing.create.street_address.label', 'Street address')}
                    required
                    disabled={!enterAddressManually}
                  />
                </Column>
              </Columns>
              <Columns space="gutter" collapseBelow="tablet">
                <Column width="1/2">
                  <Stack space="xsmall">
                    <FieldInput
                      name="suite"
                      label={
                        isMultiUnit
                          ? t('l.listing.create.plan_name.label', 'Plan name')
                          : isApartment
                            ? t('l.listing.create.unit_number.label', 'Unit number')
                            : t('l.listing.create.unit_name_number.label', 'Unit name / number')
                      }
                      required={isMultiUnit || isApartment}
                    />
                    {!isMultiUnit && (
                      <Checkbox
                        id="hide_unit_number"
                        name="hide_unit_number"
                        checked={values.hide_unit_number}
                        label={
                          !isHouse
                            ? t('l.listing.create.hide_unit_number.label', 'Hide unit number')
                            : t(
                                'l.listing.create.hide_street_number.label',
                                'Hide unit and street number'
                              )
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const isChecked = e?.target?.checked
                          setFieldValue('hide_unit_number', isChecked)
                        }}
                      />
                    )}
                  </Stack>
                </Column>
                <Column width="1/2">
                  <FieldInput
                    name="building_name"
                    label={
                      isApartment
                        ? t('l.listing.create.building_name.label', 'Building name')
                        : t('l.listing.create.property_name.label', 'Property name')
                    }
                    required={isApartment}
                    disabled={!enterAddressManually}
                  />
                </Column>
              </Columns>
              <Columns space="gutter" collapseBelow="tablet">
                <Column width="1/2">
                  <FieldInput
                    name="city"
                    label={t('l.listing.create.city.label', 'City')}
                    required
                    disabled={!enterAddressManually}
                  />
                </Column>
                <Column width="1/2">
                  <Dropdown
                    name="province"
                    label={isUnitedStatesSelected ? t('State') : t('Province')}
                    aria-labelledby="province"
                    required
                    options={formattedStates}
                    disabled={!enterAddressManually}
                  />
                </Column>
              </Columns>
              <Columns space="gutter" collapseBelow="tablet">
                <Column width="1/2">
                  <FieldInput
                    name="zip"
                    label={
                      isUnitedStatesSelected
                        ? t('Zip code')
                        : t('l.listing.create.postal_code.label', 'Postal code')
                    }
                    required
                    disabled={!enterAddressManually}
                    maxLength={isUnitedStatesSelected ? 10 : 7}
                    mask={(value: string) => {
                      if (isUnitedStatesSelected) return (value ?? '').trim()
                      return (value ?? '')
                        .toUpperCase()
                        .replace(/[^A-Za-z0-9]/g, '')
                        .replace(/(.{1})(.{1})(.{1})(.{1})(.{1})(.{1})?.*/, '$1$2$3 $4$5$6')
                        .trim()
                    }}
                  />
                </Column>
                <Column width="1/2">
                  <Dropdown
                    name="country"
                    aria-labelledby="country"
                    label={t('l.listing.create.country.label', 'Country')}
                    required
                    disabled={!enterAddressManually}
                    options={[
                      { value: 'ca', label: 'Canada' },
                      { value: 'us', label: 'United States' }
                    ]}
                  />
                </Column>
              </Columns>
            </>
          )}
          <ListingMap coordinates={markerCoords} zoom={zoom} />
        </Stack>
      )}
    </FormBox>
  )
}

export default LocationSection
